import { HttpClient, HttpEvent, HttpParams, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { enpointCervinoWeb } from 'src/app/configuration/enpointsCervino';
import { CustomListDropdown } from 'src/app/interfaces/custom-list/custom-list-dropdown.interface';
import { CostCenterImportModel } from 'src/app/models/imports/cost-center-import.model';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { ArticleSelectionTableModel } from 'src/app/models/tables/archive/article-selection-table.model';
import { environment } from 'src/environments/environment';
import { CostCenterModel } from './../../models/archive/cost-center.model';
import { LoadPreInvoicingModel } from 'src/app/models/invoicing/load-preinvoicing.models';
import { PreInvoicingAlertModel } from 'src/app/models/invoicing/pre-invoicing-alert.models';

@Injectable({
  providedIn: 'root',
})
export class CostCenterService {
  constructor(private _httpClient: HttpClient) { }

  createCostCenter(
    costCenterContractModel: CostCenterModel
  ): Observable<CostCenterModel> {
    return this._httpClient.post<CostCenterModel>(
      `${environment.apiCervinoBackend}${enpointCervinoWeb.costCenter.CreateCostCenter}`,
      costCenterContractModel
    );
  }

  deleteCostCenter(costCenterId: number): Observable<CostCenterModel> {
    return this._httpClient.delete<CostCenterModel>(
      `${environment.apiCervinoBackend}${enpointCervinoWeb.costCenter.DeleteCostCenter}/${costCenterId}`
    );
  }

  downloadGuideImport(): Observable<HttpEvent<Blob>> {
    return this._httpClient.request(
      new HttpRequest(
        'GET',
        `${environment.apiCervinoBackend}${enpointCervinoWeb.costCenter.DownloadGuideImport}`,
        null,
        { reportProgress: true, responseType: 'blob' }
      )
    );
  }

  exportGeneralConsultation(
    filters: ColumnResponseModel[],
    columnSort: string | null,
    ascendant: boolean
  ): Observable<Blob> {
    const params = new HttpParams()
      .set('columnSort', columnSort ?? '')
      .set('ascendant', ascendant.toString());
    return this._httpClient.post(
      `${environment.apiCervinoBackend}${enpointCervinoWeb.costCenter.ExportGeneralConsultation}`,
      filters,
      {
        params,
        responseType: 'blob',
      }
    );
  }

  exportImportErrors(alerts: PreInvoicingAlertModel[]): Observable<Blob> {
    return this._httpClient.post(`${environment.apiCervinoBackend}${enpointCervinoWeb.costCenter.ExportImportErrors}`, alerts,
      {
        responseType: 'blob'
      }
    );
  }

  getAllCostCentersByKeysForSelectAsync(branchId: number, clientId: number, constructionSiteId: number): Observable<Array<any>> {
    return this._httpClient.get<Array<any>>(
      `${environment.apiCervinoBackend}${enpointCervinoWeb.costCenter.GetAllCostCentersByKeysForSelectAsync}/${branchId}/${clientId}/${constructionSiteId}`
    );
  }

  getAllCostCentersForSelectAsync(): Observable<Array<any>> {
    return this._httpClient.get<Array<any>>(
      `${environment.apiCervinoBackend}${enpointCervinoWeb.costCenter.GetAllCostCentersForSelectAsync}`
    );
  }

  getAllCostCentersForSelectByClientAsync(clientId: number, constructionSiteId: number): Observable<Array<any>> {
    return this._httpClient.get<Array<any>>(
      `${environment.apiCervinoBackend}${enpointCervinoWeb.costCenter.GetAllCostCentersForSelectByClientAsync}/${clientId}/${constructionSiteId}`
    );
  }

  getAllCostCentersWithPagination(
    page: number,
    limit: number,
    columnSort: string | null,
    ascendant: boolean,
    filters: ColumnResponseModel[]
  ): Observable<PagedResponseModel<any>> {
    const params = new HttpParams()
      .set('page', page)
      .set('limit', limit)
      .set('columnSort', columnSort || '')
      .set('ascendant', ascendant);
    return this._httpClient.post<PagedResponseModel<any>>(
      `${environment.apiCervinoBackend}${enpointCervinoWeb.costCenter.GetAllCostCentersWithPagination}`,
      filters,
      { params: params }
    );
  }

  getAllCostCentersWorkshop(): Observable<Array<CostCenterModel>> {
    return this._httpClient.get<Array<CostCenterModel>>(
      `${environment.apiCervinoBackend}${enpointCervinoWeb.costCenter.GetAllCostCentersWorkshop}`
    );
  }

  getArticleForSelectAsync(articleId: number): Observable<any> {
    const params = new HttpParams().set('articleId', articleId);
    return this._httpClient.get<any>(
      `${environment.apiCervinoBackend}${enpointCervinoWeb.costCenter.GetArticlesForSelect}`,
      { params: params }
    );
  }

  getArticlesForCostCenters(
    page: number,
    limit: number,
    columnSort: string | null,
    ascendant: boolean,
    filters: ColumnResponseModel[]
  ): Observable<PagedResponseModel<ArticleSelectionTableModel>> {
    const params = new HttpParams()
      .set('page', page)
      .set('limit', limit)
      .set('columnSort', columnSort || '')
      .set('ascendant', ascendant);
    return this._httpClient.post<
      PagedResponseModel<ArticleSelectionTableModel>
    >(
      `${environment.apiCervinoBackend}${enpointCervinoWeb.costCenter.GetArticlesForCostCenters}`,
      filters,
      { params: params }
    );
  }

  getColumnsForArticles(): Observable<ColumnResponseModel[]> {
    return this._httpClient.get<ColumnResponseModel[]>(
      `${environment.apiCervinoBackend}${enpointCervinoWeb.costCenter.GetColumnsForArticles}`
    );
  }

  getColumnsForGeneralConsultation(): Observable<ColumnResponseModel[]> {
    return this._httpClient.get<ColumnResponseModel[]>(
      `${environment.apiCervinoBackend}${enpointCervinoWeb.costCenter.GetColumnsForGeneralConsultation}`
    );
  }

  getConceptInvoicingsForSelect(): Observable<Array<CustomListDropdown>> {
    return this._httpClient.get<Array<CustomListDropdown>>(
      `${environment.apiCervinoBackend}${enpointCervinoWeb.costCenter.GetConceptInvoicingsForSelect}`
    );
  }

  getConstructionSitesForSelect(
    branchId: number
  ): Observable<Array<CustomListDropdown>> {
    const params = new HttpParams().set('branchId', branchId);
    return this._httpClient.get<Array<CustomListDropdown>>(
      `${environment.apiCervinoBackend}${enpointCervinoWeb.costCenter.GetConstructionSitesForSelect}`,
      { params: params }
    );
  }

  getCostCenterByBranchByEnterpriseByClient(
    branchId: number,
    enterpriseId: number,
    clientId: number
  ): Observable<Array<CostCenterModel>> {
    return this._httpClient.get<Array<CostCenterModel>>(
      `${environment.apiCervinoBackend}${enpointCervinoWeb.costCenter.GetCostCenterByBranchByEnterpriseByClient}/${branchId}/${enterpriseId}/${clientId}`
    );
  }

  getCostCenterByBranchId(
    branchId: number
  ): Observable<Array<CostCenterModel>> {
    return this._httpClient.get<Array<CostCenterModel>>(
      `${environment.apiCervinoBackend}${enpointCervinoWeb.costCenter.GetCostCenterByBranchId}/${branchId}`
    );
  }

  getCostCenterByClient(clientId: number): Observable<Array<CostCenterModel>> {
    return this._httpClient.get<Array<CostCenterModel>>(
      `${environment.apiCervinoBackend}${enpointCervinoWeb.costCenter.GetCostCenterByClientId}/${clientId}`
    );
  }

  getCostCenterByCode(
    branchId: number,
    code: number
  ): Observable<CostCenterModel> {
    return this._httpClient.get<CostCenterModel>(
      `${environment.apiCervinoBackend}${enpointCervinoWeb.costCenter.GetCostCenterByCode}/${branchId}/${code}`
    );
  }

  getCostCenterById(costCenterId: number): Observable<CostCenterModel> {
    return this._httpClient.get<CostCenterModel>(
      `${environment.apiCervinoBackend}${enpointCervinoWeb.costCenter.GetCostCenterById}/${costCenterId}`
    );
  }

  getCostCentersForInvoicingCutoff(
    invoicingCutoffId: number
  ): Observable<Array<CostCenterModel>> {
    return this._httpClient.get<Array<CostCenterModel>>(
      `${environment.apiCervinoBackend}${enpointCervinoWeb.costCenter.GetCostCentersForInvoicingCutoff}/${invoicingCutoffId}`
    );
  }

  getCostCenterTypesForSelect(): Observable<Array<CustomListDropdown>> {
    return this._httpClient.get<Array<CustomListDropdown>>(
      `${environment.apiCervinoBackend}${enpointCervinoWeb.costCenter.GetCostCenterTypesForSelect}`
    );
  }

  getEnterpriseAndClientByConstructionSiteIdAsync(
    constructionSiteId: number
  ): Observable<any> {
    const params = new HttpParams().set(
      'constructionSiteId',
      constructionSiteId
    );
    return this._httpClient.get<any>(
      `${environment.apiCervinoBackend}${enpointCervinoWeb.costCenter.GetEnterpriseAndClientByConstructionSiteIdAsync}`,
      { params: params }
    );
  }

  getFirstCostCenterByKeysAsync(branchId: number): Observable<CostCenterModel> {
    const params = new HttpParams().set('branchId', branchId);
    return this._httpClient.get<CostCenterModel>(
      `${environment.apiCervinoBackend}${enpointCervinoWeb.costCenter.GetFirstCostCenterByKeys}`,
      { params: params }
    );
  }

  getInvoicingModelsForSelectAsync(): Observable<Array<CustomListDropdown>> {
    return this._httpClient.get<Array<CustomListDropdown>>(
      `${environment.apiCervinoBackend}${enpointCervinoWeb.costCenter.GetInvoicingModelsForSelect}`
    );
  }

  getIvaTypesForSelect(): Observable<Array<CustomListDropdown>> {
    return this._httpClient.get<Array<CustomListDropdown>>(
      `${environment.apiCervinoBackend}${enpointCervinoWeb.costCenter.GetIvaTypesForSelect}`
    );
  }

  getLastCostCenterByKeysAsync(branchId: number): Observable<CostCenterModel> {
    const params = new HttpParams().set('branchId', branchId);
    return this._httpClient.get<CostCenterModel>(
      `${environment.apiCervinoBackend}${enpointCervinoWeb.costCenter.GetLastCostCenterByKeys}`,
      { params: params }
    );
  }

  getNextCostCenterByKeysAsync(
    branchId: number,
    code: number
  ): Observable<CostCenterModel> {
    const params = new HttpParams().set('branchId', branchId).set('code', code);
    return this._httpClient.get<CostCenterModel>(
      `${environment.apiCervinoBackend}${enpointCervinoWeb.costCenter.GetNextCostCenterByKeys}`,
      { params: params }
    );
  }

  getPreviusCostCenterByKeysAsync(
    branchId: number,
    code: number
  ): Observable<CostCenterModel> {
    const params = new HttpParams().set('branchId', branchId).set('code', code);
    return this._httpClient.get<CostCenterModel>(
      `${environment.apiCervinoBackend}${enpointCervinoWeb.costCenter.GetPreviusCostCenterByKeys}`,
      { params: params }
    );
  }

  getPriceListsForSelect(
    branchId: number
  ): Observable<Array<CustomListDropdown>> {
    const params = new HttpParams().set('branchId', branchId);
    return this._httpClient.get<Array<CustomListDropdown>>(
      `${environment.apiCervinoBackend}${enpointCervinoWeb.costCenter.GetPriceListsForSelect}`,
      { params: params }
    );
  }

  importCostCenters(costCenterImports: Array<CostCenterImportModel>): Observable<LoadPreInvoicingModel> {
    return this._httpClient.post<LoadPreInvoicingModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.costCenter.ImportCostCenters}`,
      costCenterImports);
  }

  updateCostCenter(
    costCenterContractModel: CostCenterModel
  ): Observable<CostCenterModel> {
    return this._httpClient.put<CostCenterModel>(
      `${environment.apiCervinoBackend}${enpointCervinoWeb.costCenter.UpdateCostCenter}`,
      costCenterContractModel
    );
  }
}
